import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Switch,
  TextField,
  Typography,
} from "@mui/material"
import {
  DocumentData,
  QueryDocumentSnapshot,
  Timestamp,
  addDoc,
  collection,
  endAt,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
  startAt,
  updateDoc,
  doc as firestoreDoc,
} from "firebase/firestore"
import React, { useCallback, useEffect, useState } from "react"

import MUIDataTable from "mui-datatables"
import { auth, db } from "../../firebase"
import {
  CouponCampaignFormData,
  buildCouponCampaignPayload,
  validateCouponCampaignForm,
} from "./couponCampaignFormUtils"

type AdminUser = {
  displayName: string
  email: string
  id: string
}

type CouponCampaign = {
  active: boolean
  campaignName: string
  couponTitle: string
  id: string
  intervalDays: number
  nextRunAt: Timestamp | null
  recipientUserIds: string[]
}

const USERS_PAGE_SIZE = 10

const EMPTY_FORM: CouponCampaignFormData = {
  campaignName: "",
  couponDescription: "",
  couponTitle: "",
  intervalDays: 7,
  startAt: "",
}

const AdminCouponCampaignsPage: React.FC = () => {
  const [users, setUsers] = useState<AdminUser[]>([])
  const [selectedUsers, setSelectedUsers] = useState<string[]>([])
  const [lastVisible, setLastVisible] = useState<QueryDocumentSnapshot<DocumentData> | null>(null)
  const [isLoadingUsers, setIsLoadingUsers] = useState(false)

  const [formData, setFormData] = useState<CouponCampaignFormData>(EMPTY_FORM)
  const [formErrors, setFormErrors] = useState<string[]>([])
  const [isSaving, setIsSaving] = useState(false)
  const [saveSuccess, setSaveSuccess] = useState(false)

  const [campaigns, setCampaigns] = useState<CouponCampaign[]>([])
  const [isLoadingCampaigns, setIsLoadingCampaigns] = useState(false)
  const [togglingCampaignId, setTogglingCampaignId] = useState<string | null>(null)

  const fetchUsers = useCallback(async (reset = false, search = "") => {
    setIsLoadingUsers(true)
    try {
      const baseQuery = search
        ? query(
            collection(db, "users"),
            orderBy("displayName"),
            startAt(search),
            endAt(search + ""),
            limit(USERS_PAGE_SIZE),
          )
        : query(
            collection(db, "users"),
            orderBy("displayName"),
            ...(reset ? [] : [startAfter(lastVisible)]),
            limit(USERS_PAGE_SIZE),
          )

      const snapshot = await getDocs(baseQuery)
      const usersData = snapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...(docSnap.data() as Record<string, any>),
      })) as AdminUser[]

      setUsers((previous) => (reset ? usersData : [...previous, ...usersData]))
      setLastVisible(snapshot.docs[snapshot.docs.length - 1] || null)
    } catch (error) {
      console.error("Error fetching users:", error)
    } finally {
      setIsLoadingUsers(false)
    }
    // lastVisible is intentionally excluded: it is read once per call and
    // including it would recreate this callback (and re-trigger effects) on every page fetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const fetchCampaigns = useCallback(async () => {
    setIsLoadingCampaigns(true)
    try {
      const snapshot = await getDocs(query(collection(db, "couponCampaigns"), orderBy("campaignName")))
      setCampaigns(
        snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...(docSnap.data() as Record<string, any>) })) as CouponCampaign[],
      )
    } catch (error) {
      console.error("Error fetching coupon campaigns:", error)
    } finally {
      setIsLoadingCampaigns(false)
    }
  }, [])

  useEffect(() => {
    fetchUsers(true)
    fetchCampaigns()
  }, [fetchUsers, fetchCampaigns])

  const handleTableChange = (action: string, tableState: any) => {
    if (action === "rowSelectionChange") {
      const selectedData = tableState.selectedRows.data.map((row: any) => users[row.index].id)
      setSelectedUsers(selectedData)
    } else if (action === "search") {
      fetchUsers(true, tableState.searchText || "")
    }
  }

  const handleSubmit = async () => {
    const errors = validateCouponCampaignForm(formData, selectedUsers)
    setFormErrors(errors)
    setSaveSuccess(false)
    if (errors.length > 0) {
      return
    }

    setIsSaving(true)
    try {
      const payload = buildCouponCampaignPayload({
        createdBy: auth.currentUser?.uid || "",
        formData,
        recipientUserIds: selectedUsers,
      })
      await addDoc(collection(db, "couponCampaigns"), {
        ...payload,
        lastRunAt: null,
        nextRunAt: Timestamp.fromDate(payload.nextRunAt),
      })
      setSaveSuccess(true)
      setFormData(EMPTY_FORM)
      setSelectedUsers([])
      await fetchCampaigns()
    } catch (error: any) {
      setFormErrors([error?.message || "Failed to create the coupon campaign."])
    } finally {
      setIsSaving(false)
    }
  }

  const toggleCampaignActive = async (campaign: CouponCampaign) => {
    setTogglingCampaignId(campaign.id)
    try {
      await updateDoc(firestoreDoc(db, "couponCampaigns", campaign.id), { active: !campaign.active })
      setCampaigns((previous) =>
        previous.map((item) => (item.id === campaign.id ? { ...item, active: !item.active } : item)),
      )
    } catch (error) {
      console.error("Error toggling coupon campaign:", error)
    } finally {
      setTogglingCampaignId(null)
    }
  }

  const userColumns = [
    { name: "displayName", label: "Name" },
    { name: "email", label: "Email" },
    { name: "id", label: "User ID" },
  ]

  const userOptions = {
    filter: false,
    responsive: "standard" as const,
    selectableRows: "multiple" as const,
    selectableRowsOnClick: true,
    onTableChange: handleTableChange,
    search: true,
    searchPlaceholder: "Search users",
  }

  const campaignColumns = [
    { name: "campaignName", label: "Campaign" },
    { name: "couponTitle", label: "Coupon" },
    {
      name: "recipientUserIds",
      label: "Recipients",
      options: {
        customBodyRender: (value: string[]) => value?.length || 0,
      },
    },
    { name: "intervalDays", label: "Every (days)" },
    {
      name: "nextRunAt",
      label: "Next Run",
      options: {
        customBodyRender: (value: Timestamp | null) => (value ? value.toDate().toLocaleString() : "-"),
      },
    },
    {
      name: "active",
      label: "Active",
      options: {
        customBodyRenderLite: (dataIndex: number) => {
          const campaign = campaigns[dataIndex]
          return (
            <Switch
              checked={campaign.active}
              disabled={togglingCampaignId === campaign.id}
              onChange={() => toggleCampaignActive(campaign)}
              color="primary"
            />
          )
        },
      },
    },
  ]

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>
        Recurring Coupon Campaigns
      </Typography>

      <MUIDataTable
        title="Select Recipients"
        data={users}
        columns={userColumns}
        options={userOptions}
      />
      {isLoadingUsers && (
        <Box mt={1}>
          <CircularProgress size={20} />
        </Box>
      )}
      <Box mt={1}>
        <Chip label={`${selectedUsers.length} recipient(s) selected`} />
      </Box>

      <Box mt={3}>
        <TextField
          label="Campaign Name"
          value={formData.campaignName}
          onChange={(e) => setFormData({ ...formData, campaignName: e.target.value })}
          fullWidth
          margin="normal"
        />
        <TextField
          label="Coupon Title"
          value={formData.couponTitle}
          onChange={(e) => setFormData({ ...formData, couponTitle: e.target.value })}
          fullWidth
          margin="normal"
        />
        <TextField
          label="Coupon Description"
          value={formData.couponDescription}
          onChange={(e) => setFormData({ ...formData, couponDescription: e.target.value })}
          fullWidth
          multiline
          rows={3}
          margin="normal"
        />
        <TextField
          label="Start Date"
          type="datetime-local"
          value={formData.startAt}
          onChange={(e) => setFormData({ ...formData, startAt: e.target.value })}
          fullWidth
          margin="normal"
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="Repeat Every (days)"
          type="number"
          value={formData.intervalDays}
          onChange={(e) => setFormData({ ...formData, intervalDays: e.target.value })}
          fullWidth
          margin="normal"
          inputProps={{ min: 1 }}
        />
      </Box>

      {formErrors.length > 0 && (
        <Box mt={2}>
          {formErrors.map((error) => (
            <Alert severity="error" key={error} sx={{ mb: 1 }}>
              {error}
            </Alert>
          ))}
        </Box>
      )}
      {saveSuccess && (
        <Box mt={2}>
          <Alert severity="success">Coupon campaign created.</Alert>
        </Box>
      )}

      <Button
        variant="contained"
        color="primary"
        onClick={handleSubmit}
        disabled={isSaving}
        fullWidth
        sx={{ mt: 3 }}
      >
        {isSaving ? <CircularProgress size={24} /> : "Create Campaign"}
      </Button>

      <Box mt={5}>
        <Typography variant="h5" gutterBottom>
          Existing Campaigns
        </Typography>
        {isLoadingCampaigns ? (
          <CircularProgress size={20} />
        ) : (
          <MUIDataTable
            title="Campaigns"
            data={campaigns}
            columns={campaignColumns}
            options={{ filter: false, search: false, selectableRows: "none" }}
          />
        )}
      </Box>
    </Box>
  )
}

export default AdminCouponCampaignsPage
